// components/MyPage/MyPosts.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Pagination,
  ButtonBase, Stack, CircularProgress, Card, CardContent, useMediaQuery,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import BlockIcon from '@mui/icons-material/Block';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import api from '../../api/axios';

const PAGE_SIZE = 10;

export default function MyPosts() {
  const [posts, setPosts] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    api.get('/api/posts/my-posts/')
      .then(res => setPosts(res.data.my_posts || []))
      .catch(() => setPosts([]));
  }, []);

  if (posts === null) return <CircularProgress />;
  if (posts.length === 0) return <Typography sx={{ p: 3 }}>작성한 게시글이 없습니다.</Typography>;

  const pageCount = Math.ceil(posts.length / PAGE_SIZE);
  const slice = posts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const titleLimit = isMobile ? 6 : 10;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 1 }}>
      {isMobile ? (
        <Stack spacing={2}>
          {slice.map((row) => {
            const blind = row.is_hidden;
            const fullTitle = row.title || '(제목 없음)';
            const shortTitle = fullTitle.length > titleLimit
              ? fullTitle.slice(0, titleLimit) + '…'
              : fullTitle;

            return (
              <Card
                key={row.id}
                variant="outlined"
                sx={{
                  backgroundColor: blind ? '#f5f5f5' : 'white',
                  cursor: blind ? 'default' : 'pointer',
                  opacity: blind ? 0.6 : 1,
                }}
                onClick={() => !blind && navigate(`/main/posts/${row.id}`)}
              >
                <CardContent>
                  <Stack spacing={1}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      title={fullTitle}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'left',
                        alignSelf: 'flex-start',
                      }}
                    >
                      {blind ? '블라인드된 게시글입니다' : shortTitle}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'left', alignSelf: 'flex-start' }}
                    >
                      {dayjs(row.created_at).format('YY.MM.DD HH:mm')}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <FavoriteIcon fontSize="small" />
                        <Typography variant="body2">{row.like_count ?? 0}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ChatBubbleIcon fontSize="small" />
                        <Typography variant="body2">{row.comment_count ?? 0}</Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>제목</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>작성일</TableCell>
                  <TableCell align="center" sx={{ width: 92 }}>❤️</TableCell>
                  <TableCell align="center" sx={{ width: 92 }}>💬</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slice.map((row) => {
                  const blind = row.is_hidden;
                  const fullTitle = row.title || '(제목 없음)';
                  const shortTitle = fullTitle.length > titleLimit
                    ? fullTitle.slice(0, titleLimit) + '…'
                    : fullTitle;

                  return (
                    <TableRow
                      key={row.id}
                      hover={!blind}
                      sx={{ cursor: blind ? 'default' : 'pointer' }}
                    >
                      <TableCell>
                        <ButtonBase
                          disabled={blind}
                          onClick={() => !blind && navigate(`/main/posts/${row.id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            textAlign: 'left',
                            color: 'inherit',
                            p: 0,
                            justifyContent: 'flex-start',
                          }}
                        >
                          {blind ? (
                            <>
                              <BlockIcon fontSize="inherit" color="error" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="text.disabled">
                                블라인드된 게시글입니다
                              </Typography>
                            </>
                          ) : (
                            <>
                              <ArticleIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                              <Typography
                                variant="body2"
                                title={fullTitle}
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'left',
                                }}
                              >
                                {shortTitle}
                              </Typography>
                            </>
                          )}
                        </ButtonBase>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {dayjs(row.created_at).format('YY-MM-DD')}
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(row.created_at).format('HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{row.like_count ?? 0}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{row.comment_count ?? 0}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            shape="rounded"
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
